import React, { useState, useRef, useEffect } from 'react'

import { DownOutlined, UpOutlined } from 'shared/ui'
import styled from 'styled-components'

export interface SortOption {
  key: string
  label: string
  icon?: React.ReactNode
  direction?: 'asc' | 'desc'
}

export interface SortValue {
  key: string
  direction: 'asc' | 'desc'
}

interface SortingControlProps {
  options: SortOption[]
  value?: SortValue
  onChange?: (value: SortValue) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  showDirection?: boolean
  allowDirectionToggle?: boolean
}

export const SortingControl: React.FC<SortingControlProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Sort by',
  className,
  disabled = false,
  showDirection = true,
  allowDirectionToggle = true
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const selectedOption = options.find(option => option.key === value?.key)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen)
    }
  }

  const handleOptionClick = (optionKey: string) => {
    const currentDirection = value?.key === optionKey ? value.direction : 'desc'
    onChange?.({ key: optionKey, direction: currentDirection })
    setIsOpen(false)
  }

  const handleDirectionToggle = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (value && allowDirectionToggle && !disabled) {
      const newDirection = value.direction === 'asc' ? 'desc' : 'asc'
      onChange?.({ ...value, direction: newDirection })
    }
  }

  const getSortIcon = (direction?: 'asc' | 'desc') => {
    if (!showDirection) return null
    if (direction === 'asc') {
      return <SSortOrderIcon $direction="asc" />
    } else if (direction === 'desc') {
      return <SSortOrderIcon $direction="desc" />
    }
    return null
  }

  return (
    <SContainer ref={dropdownRef}
      className={className}
    >
      <SControlsWrapper>
        {showDirection && value && (
          <SDirectionButton
            onClick={handleDirectionToggle}
            disabled={disabled || !allowDirectionToggle}
          >
            {getSortIcon(value.direction)}
          </SDirectionButton>
        )}

        <STrigger
          onClick={handleToggle}
          disabled={disabled}
          className={isOpen ? 'open' : ''}
        >
          <SText>
            {selectedOption ? selectedOption.label : placeholder}
          </SText>

          <SIcon>
            {isOpen ? <UpOutlined /> : <DownOutlined />}
          </SIcon>
        </STrigger>
      </SControlsWrapper>

      {isOpen && (
        <SDropdown>
          {options.map((option) => (
            <SOption
              key={option.key}
              onClick={() => handleOptionClick(option.key)}
              className={option.key === value?.key ? 'selected' : ''}
            >
              <SOptionText>
                {option.label}
              </SOptionText>

              <SOptionIcon>
                {option.icon || getSortIcon(option.direction)}
              </SOptionIcon>
            </SOption>
          ))}
        </SDropdown>
      )}
    </SContainer>
  )
}

const SContainer = styled.div`
  position: relative;
  display: inline-block;
`

const SControlsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const STrigger = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 12px 5px 0;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'SF Pro Text', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 14px;
  line-height: 22px;
  color: var(--color-black);
  width: fit-content;
  white-space: nowrap;

  &:focus {
    outline: none;
  }

  &.open {
    color: var(--color-primary);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    background: transparent;
  }
`

const SText = styled.span`
  flex: 1;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const SIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  color: var(--color-black);
  transition: color 0.2s ease;

  ${STrigger}:hover & {
    color: var(--color-black);
  }
  
  ${STrigger}.open & {
    color: var(--color-primary);
  }
`

const SDirectionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--color-black);

  &:hover {
    background-color: var(--color-gray-100);
  }

  &:active {
    background-color: var(--color-gray-100);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    background: transparent;
  }
`

const SDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: #ffffff;
  border: 1px solid var(--border-color-secondary);
  border-radius: 8px;
  box-shadow: 0px 6px 16px 0px rgba(0, 0, 0, 0.08), 0px 3px 6px -4px rgba(0, 0, 0, 0.12), 0px 9px 28px 8px rgba(0, 0, 0, 0.05);
  z-index: 1000;
  margin-top: 4px;
  padding: 4px;
  min-width: 120px;
`

const SOption = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 5px 12px;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  font-family: 'SF Pro Text', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 14px;
  line-height: 22px;
  color: var(--color-black);

  &:hover {
    color: var(--color-primary);
  }
`

const SOptionText = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const SOptionIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  color: var(--color-black);
`

interface SSortOrderIconProps {
  $direction?: 'asc' | 'desc'
}

const SSortOrderIcon = styled.div<SSortOrderIconProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  background-image: url('/images/sort.svg');
  background-size: 12px 12px;
  background-repeat: no-repeat;
  background-position: center;
  transform: ${props => props.$direction === 'desc' ? 'rotate(180deg)' : 'rotate(0deg)'};
`
